// src/pages/dashboards/practitioner/PractitionerCodes.jsx
// Issue and manage connect codes. Clients redeem a code to grant the practitioner
// read-only access to their deductions. Practitioner-issued only, no
// scan usage bars (practitioners don't have a scan pool).

import React, { useEffect, useState } from "react";
import * as api from "../../../api/practitioners";
import { Card, Button, Badge, Input, Field, Modal, Loading, EmptyState } from "../../../components/ui";
import { date, relative } from "../../../lib/format";
import { Plus } from "../../../components/icons";
import { useToast } from "../../../context/ToastContext";

const codeState = (c) => {
  if (!c.is_active) return { tone: "gray", label: "revoked" };
  if (c.expires_at && new Date(c.expires_at) < new Date()) return { tone: "gray", label: "expired" };
  if (c.max_uses != null && c.used_count >= c.max_uses) return { tone: "gray", label: "used up" };
  return { tone: "green", label: "active" };
};

export default function PractitionerCodes() {
  const toast = useToast();
  const [codes, setCodes] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = () =>
    api.listCodes()
      .then(setCodes)
      .catch((e) => { toast.error(e.message); setCodes([]); });

  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    try {
      await api.revokeCode(id);
      toast.success("Code revoked");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code).then(
      () => toast.success("Code copied"),
      () => toast.error("Could not copy")
    );
  };

  return (
    <div className="space-y-8">
      {/* connect codes */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-display font-bold">Connect codes</h1>
          <Button onClick={() => setShowNew(true)}><Plus size={16} /> New code</Button>
        </div>

        <p className="text-sm text-tb-muted mb-5">
          Share a code with your clients. When they redeem it in the taxpack app, you get
          read-only access to their tax deductions and receipts.
        </p>

        {!codes ? (
          <Loading label="Loading codes" />
        ) : codes.length === 0 ? (
          <Card className="p-2">
            <EmptyState title="No connect codes yet" hint="Create one and share it with your clients to link them." />
          </Card>
        ) : (
          <Card className="divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
            {codes.map((c) => {
              const st = codeState(c);
              return (
                <div key={c.id} className="px-4 sm:px-5 py-4 flex items-center gap-4">
                  <button
                    onClick={() => copy(c.code)}
                    title="Copy code"
                    className="font-mono text-lg tracking-widest font-bold hover:text-tb-brand transition-colors"
                  >
                    {c.code}
                  </button>
                  <div className="min-w-0 flex-1 text-sm text-tb-muted">
                    <span>{c.used_count}{c.max_uses != null ? ` / ${c.max_uses}` : ""} used</span>
                    {c.expires_at && <span> · expires {date(c.expires_at)}</span>}
                  </div>
                  <Badge tone={st.tone}>{st.label}</Badge>
                  {st.label === "active" && (
                    <Button variant="ghost" onClick={() => revoke(c.id)}>Revoke</Button>
                  )}
                </div>
              );
            })}
          </Card>
        )}
      </section>

      <NewCodeModal open={showNew} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
    </div>
  );
}

// -- new code modal -----------------------------------------------------------
function NewCodeModal({ open, onClose, onCreated }) {
  const toast = useToast();
  const [maxUses, setMaxUses] = useState("");
  const [expiresDays, setExpiresDays] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setMaxUses(""); setExpiresDays(""); } }, [open]);

  const create = async () => {
    setBusy(true);
    try {
      const body = {};
      if (maxUses) body.max_uses = parseInt(maxUses, 10);
      if (expiresDays) body.expires_in_days = parseInt(expiresDays, 10);
      await api.issueCode(body);
      toast.success("Code created");
      onCreated();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="New connect code">
      <div className="space-y-4">
        <Field label="Max uses" hint="Leave blank for unlimited.">
          <Input type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="Unlimited" />
        </Field>
        <Field label="Expires in (days)" hint="Leave blank for no expiry.">
          <Input type="number" min="1" max="365" value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)} placeholder="Never" />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={create} disabled={busy}>Create code</Button>
        </div>
      </div>
    </Modal>
  );
}